import React from 'react';
import { gql, useQuery } from '@apollo/client';
import { ResponsiveContainer, FunnelChart, Funnel, Tooltip, LabelList } from 'recharts';
import { FormattedMessage, useIntl } from 'react-intl';
import { getDealRoomBrandedName } from '../../../../utils/locale';

const GET_DEAL_ROOM_INSIGHTS = gql`
  query GetDealRoomInsights {
    getCurrentUser {
      id
      can_create_meeting_room
      company { id name deal_room_branded_name deal_room_branded_name_fr }
      inventory_user { id deals { id number_of_invites number_of_proposals number_of_ndas number_of_negotiations matches_count deal_state creation_date } }
    }
  }
`;

// Utility: lighten a color by % (supports hex or rgb/rgba)
const lightenColor = (color, percent) => {
  try {
    let r, g, b;
    if (!color) return null;
    const c = String(color).trim();
    if (c.startsWith('#')) {
      let hex = c.replace('#', '');
      if (hex.length === 3) {
        hex = hex.split('').map((h) => h + h).join('');
      }
      const bigint = parseInt(hex, 16);
      if (Number.isNaN(bigint)) return null;
      r = (bigint >> 16) & 255;
      g = (bigint >> 8) & 255;
      b = bigint & 255;
    } else if (c.startsWith('rgb')) {
      const parts = c.match(/\d+/g) || [];
      [r, g, b] = parts.map(Number);
    } else {
      return null;
    }
    const lighten = (v) => Math.min(255, Math.round(v + (255 - v) * (percent / 100)));
    return `rgb(${lighten(r)}, ${lighten(g)}, ${lighten(b)})`;
  } catch (_) {
    return null;
  }
};

const getCompanyPrimaryColor = () => {
  try {
    const root = document.documentElement;
    const cssVal = getComputedStyle(root).getPropertyValue('--succession-primary').trim();
    if (cssVal) return cssVal;
  } catch (_) {}
  return '#5489B6';
};

const getCompanyBorderColor = () => {
  try {
    const root = document.documentElement;
    const cssVal = getComputedStyle(root).getPropertyValue('--succession-border').trim();
    if (cssVal) return cssVal;
  } catch (_) {}
  return '#D1D5DB';
};

const RechartsFunnel = ({ invites=0, matches=0, proposals=0, ndas=0, negotiations=0, shapeSpec=[120,80,60,40,30], outlineOnly=false }) => {
  const seg = (name, real, shapeValue, fill, hidden=false, labelOverride=null) => ({ name, shape: Math.max(0.001, Number(shapeValue)), real: Math.max(0, Number(real||0)), label: (hidden ? undefined : (labelOverride !== null ? labelOverride : `${name}: ${Number(real||0)}`)), fill, hidden });
  const baseColor = getCompanyPrimaryColor();
  const funnelColors = Array.from({ length: 5 }, (_, i) => lightenColor(baseColor, i * 8) || baseColor);
  const hideLabels = !!outlineOnly;
  const data = [
    seg('Matches', matches, shapeSpec[0], outlineOnly ? 'transparent' : funnelColors[0], hideLabels),
    seg('Invites', invites, shapeSpec[1], outlineOnly ? 'transparent' : funnelColors[1], hideLabels),
    seg('Proposals', proposals, shapeSpec[2], outlineOnly ? 'transparent' : funnelColors[2], hideLabels), 
    seg('NDAs', ndas, shapeSpec[3], outlineOnly ? 'transparent' : funnelColors[3], hideLabels),
    seg('Negotiating', negotiations, shapeSpec[4], outlineOnly ? 'transparent' : funnelColors[4], hideLabels),
  ];
  if (!outlineOnly) {
    data.push(seg('__baseline', 0, shapeSpec[4], 'transparent', true, null));
  }
  const CenterLabel = (props) => {
    const { value, viewBox, payload } = props;
    if (!value || (payload && (payload.hidden || payload.name === '__baseline'))) return null;
    const vb = viewBox || {};
    const cx = (vb.x || 0) + (vb.width || 0) / 2;
    const cy = (vb.y || 0) + (vb.height || 0) / 2;
    return (
      <text x={cx} y={cy} fill="#f8fafc" fontSize="11" textAnchor="middle" dominantBaseline="middle" pointerEvents="none">{value}</text>
    );
  };
  const isDark = () => {
    try { return document.documentElement.classList.contains('succession-dark'); } catch (_) { return false; }
  };
  const TooltipContent = ({ active, payload }) => {
    if (!active || !payload || !payload[0]) return null;
    const p = payload[0].payload || {};
    if (p.name === '__baseline' || p.hidden) return null;
    return (
      <div className="bg-white text-succession-text-secondary border border-succession-border rounded px-2 py-1 text-xs shadow-succession-md">
        {p.name}: {p.real}
      </div>
    );
  };

  return (
    <div className="w-full" style={{ height: 220 }}>
      <div style={{ width: '70%', height: '100%', margin: '0 auto' }}>
        <ResponsiveContainer width="100%" height="100%">
          <FunnelChart>
            <Tooltip content={<TooltipContent />} />
            <Funnel dataKey="shape" data={data} isAnimationActive={false} minTrapezoidHeight={10} stroke={outlineOnly ? getCompanyBorderColor() : (isDark() ? 'transparent' : 'rgba(255,255,255,0.6)')} strokeWidth={outlineOnly ? 1.5 : 1}>
              <LabelList position="center" dataKey="label" content={<CenterLabel />} />
            </Funnel>
          </FunnelChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const DealRoomInsights = () => {
  const intl = useIntl();
  const { data, loading, error } = useQuery(GET_DEAL_ROOM_INSIGHTS, { fetchPolicy: 'cache-first' });
  if (loading) return <div className="w-full h-full min-h-[200px] animate-pulse bg-transparent rounded-md" aria-hidden="true" />;
  if (error) return <div className="text-succession-error text-xs"><FormattedMessage id="succession.deal_room_insights.error" /></div>;

  const cu = data?.getCurrentUser || {};
  const deals = cu?.inventory_user?.deals || [];
  const activeDeal = deals.find((d) => String(d.deal_state).toLowerCase() !== 'closed') || deals[0];
  const hasDealRoom = Boolean(activeDeal);
  const canCreate = Boolean(cu?.can_create_meeting_room);
  const word = getDealRoomBrandedName(cu?.company) || intl.formatMessage({ id: 'succession.messaging.chat_panel.deal_room_label' });

  const totalInquiries = hasDealRoom ? (
    Number(activeDeal?.number_of_invites||0) +
    Number(activeDeal?.number_of_proposals||0) +
    Number(activeDeal?.number_of_ndas||0) +
    Number(activeDeal?.number_of_negotiations||0)
  ) : 0;

  const formatAge = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const start = new Date(dateStr);
      const now = new Date();
      const ms = Math.max(0, now - start);
      const days = Math.floor(ms / (1000*60*60*24));
      if (days < 14) return `${days} day${days===1?'':'s'}`;
      const weeks = Math.floor(days / 7);
      if (weeks < 12) return `${weeks} week${weeks===1?'':'s'}`;
      const months = Math.floor(days / 30);
      return `${months} month${months===1?'':'s'}`;
    } catch(_) { return '—'; }
  };
  const timeOnMarket = hasDealRoom ? formatAge(activeDeal?.creation_date) : '—';

  return (
    <div className="h-full min-h-[200px] flex flex-col">
      <div>
        <h3 className="text-succession-text-primary m-0 p-0"><FormattedMessage id="succession.deal_room_insights.funnel_title" values={{ word }} /></h3>
        <div className="text-succession-text-secondary text-xs"><FormattedMessage id="succession.deal_room_insights.funnel_subtitle" values={{ word }} /></div>
      </div>
      <div className="mt-2 flex-1 flex items-center justify-center">
        {hasDealRoom ? (
          <RechartsFunnel
            invites={Number(activeDeal?.number_of_invites||0)}
            matches={Number(activeDeal?.matches_count||0)}
            proposals={Number(activeDeal?.number_of_proposals||0)}
            ndas={Number(activeDeal?.number_of_ndas||0)}
            negotiations={Number(activeDeal?.number_of_negotiations||0)}
          />
        ) : (
          <div className="flex flex-col items-center justify-center text-center">
            <RechartsFunnel invites={0} matches={0} proposals={0} ndas={0} negotiations={0} outlineOnly={true} />
            <p className="mt-2 text-xs text-succession-text-secondary"><FormattedMessage id="succession.deal_room_insights.empty_message" /></p>
          </div>
        )}
      </div>
      {hasDealRoom && (
        <div className="mt-2 grid grid-cols-2 gap-3 text-xs w-full">
          <div className="text-succession-text-secondary"><FormattedMessage id="succession.deal_room_insights.total_inquiries" />
            <div className="text-succession-text-primary font-semibold">{totalInquiries}</div>
          </div>
          <div className="text-right text-succession-text-secondary"><FormattedMessage id="succession.deal_room_insights.time_on_market" />
            <div className="text-succession-text-primary font-semibold">{timeOnMarket}</div>
          </div>
        </div>
      )}
      <div className="mt-auto pt-2 flex justify-center">
        {hasDealRoom && (
          <a href="/succession/deals" className="text-sm text-succession-primary hover:text-succession-primary-hover"><FormattedMessage id="succession.deal_room_insights.view_word" values={{ word }} /></a>
        )}
        {!hasDealRoom && canCreate && (
          <a href="/succession/deals" className="text-sm text-succession-primary hover:text-succession-primary-hover"><FormattedMessage id="succession.deal_room_insights.create_word" values={{ word }} /></a>
        )}
        {!hasDealRoom && !canCreate && (
          <span className="text-xs text-succession-text-secondary"><FormattedMessage id="succession.deal_room_insights.check_back" values={{ word }} /></span>
        )}
      </div>
    </div>
  );
};

export default DealRoomInsights;




